/**
 * Live Sessions — Story 7f.
 *
 * Operator surface listing every assignment currently in progress across the
 * coordinator's project(s). Per the MineTwin hookup plan:
 *   - Active assignments: `cohort_progress` filtered `status.tag === 'InProgress'`
 *   - Session status: cross-reference mp `discoverable_sessions` via
 *     `cohort_assignment_progress.session_id`
 *
 * Sits between `TraineePortal` (per-user) and `ReviewQueue` (post-completion)
 * in the orientation lifecycle. One write action: Join (Phase 4, req 4)
 * records the caller as an observer of the run's mp session and launches
 * the MineZero client in observer mode.
 */

import { useMemo, useState } from 'react';
import { useSpacetimeDB } from 'spacetimedb/react';
import { type DbConnection as MinetwinConn } from '@sdk/minetwin';
import { formatLegacyVersionTag } from '@/lib/orientationVersion';
import { pairingIdsOf, inCohortFilter } from '@/lib/cohortFilter';
import { buildObserverLaunchArgs } from '@/services/launcher/launcherArgs';
import { CohortFilterSelect } from '@/components/CohortFilterSelect';
import { useCohortEntities } from '@/hooks/useCohortEntities';
import { useGroupRuns, type GroupRunRowData } from '@/hooks/useGroupRuns';
import { useMineZeroLaunch } from '@/hooks/useMineZeroLaunch';
import { useLiveSessions, type LiveSession } from '@/hooks/useLiveSessions';
import { GroupRunLiveRow } from './GroupRunLiveRow';
import { useOrientations } from '@/hooks/useOrientations';
import { useOrientationStepOrder } from '@/hooks/useOrientationStepOrder';
import { useProjects } from '@/hooks/useProjects';
import { NoProjects } from '@/components/NoProjects';
import { NoSiteMembership } from '@/components/NoSiteMembership';
import { useMySiteMembership } from '@/hooks/useMySiteMembership';
import { useTweaks } from '@/hooks/useTweaks';
import { useAuthKit } from '@/contexts/AuthProvider';
import { useDialogs } from '@/components/dialogs/DialogProvider';
import { isRunLeader } from '@/lib/groupRunEntity';
import type { Site, Toast } from '@/data/types';
import { GatedButton } from '@/components/GatedButton';

type LiveSessionsProps = { toast: (t: Omit<Toast, 'id'>) => void };

export function LiveSessions({ toast }: LiveSessionsProps) {
  const minetwinState = useSpacetimeDB('minetwin');
  const { sessions, isReady } = useLiveSessions();
  const { sites, isReady: sitesReady } = useProjects();
  const { tweaks } = useTweaks();
  const { orientations } = useOrientations();
  const { stepsByOrientation } = useOrientationStepOrder();

  const currentSite: Site | undefined = sites[tweaks.siteIndex] ?? sites[0];
  const membership = useMySiteMembership(currentSite?.projectName ?? '');
  const { cohorts } = useCohortEntities(currentSite?.projectName);
  const [cohortFilter, setCohortFilter] = useState<bigint | null>(null);
  const launch = useMineZeroLaunch();
  const [joinBusyId, setJoinBusyId] = useState<bigint | null>(null);
  const [cancelBusyId, setCancelBusyId] = useState<bigint | null>(null);
  const dialogs = useDialogs();
  // Group rows carry only the step id; step ids are global, so resolve the
  // name across every orientation's step list (solo rows already show names).
  const stepNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const steps of stepsByOrientation.values()) for (const st of steps) m.set(st.stepId, st.displayName);
    return m;
  }, [stepsByOrientation]);
  const { user } = useAuthKit();
  const currentUserEmail = user?.email ?? '';

  /**
   * Join as observer (req 4). Two acts, both surfaced honestly:
   *  1. `join_session_as_observer` (awaited, #76 rule) records the
   *     email-keyed observer request — the participant row lands on the
   *     CLIENT's next connect, not the browser's, which is why identity
   *     is never captured here (module review fix, 2026-09-14).
   *  2. Local-API launch with `-TurboTwinObserveSession=<id>`. No queue
   *     fallback: a queued launch drops the args and would enter trainee
   *     mode. If the launcher isn't running, the join stays recorded and
   *     the toast says how to finish.
   */
  /**
   * Cancel from Live Sessions. The archive dialog ("Archive, let them
   * finish") tells coordinators to cancel what is left from here, and this
   * page only offered Join. Same reducers and wording as the Cohorts tab;
   * awaited, so the toast reports what the server did.
   */
  async function handleCancelSolo(s: LiveSession) {
    if (
      !(await dialogs.confirm({
        title: `Cancel ${s.email}'s orientation?`,
        body: 'Their session closes now and their progress stops. You can assign them again later.',
        confirmLabel: 'Cancel orientation',
        cancelLabel: 'Keep',
        danger: true,
      }))
    )
      return;
    await runCancel(s.assignmentId, `${s.email}'s orientation cancelled`, (c) =>
      c.reducers.cancelAssignment({ assignmentId: s.assignmentId }),
    );
  }

  async function handleCancelGroup(run: GroupRunRowData) {
    if (
      !(await dialogs.confirm({
        title: `Cancel group "${run.name}"?`,
        body: 'The shared session closes. Every member carries on self-paced in a session of their own, keeping their progress.',
        confirmLabel: 'Cancel group',
        cancelLabel: 'Keep',
        danger: true,
      }))
    )
      return;
    await runCancel(run.groupRunId, `"${run.name}" cancelled`, (c) =>
      c.reducers.cancelGroupRun({ groupRunId: run.groupRunId }),
    );
  }

  async function runCancel(id: bigint, doneTitle: string, call: (c: MinetwinConn) => Promise<unknown>) {
    const conn = minetwinState.getConnection() as MinetwinConn | null;
    if (!conn) {
      toast({ title: 'Not connected to MineTwin module', kind: 'bad' });
      return;
    }
    setCancelBusyId(id);
    try {
      await call(conn);
      toast({ title: doneTitle, kind: 'good' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast({ title: 'Cancel refused', sub: msg.slice(0, 200), kind: 'bad' });
    } finally {
      setCancelBusyId(null);
    }
  }

  async function handleJoin(s: LiveSession) {
    const conn = minetwinState.getConnection() as MinetwinConn | null;
    if (!conn) {
      toast({ title: 'Not connected to MineTwin module', kind: 'bad' });
      return;
    }
    setJoinBusyId(s.assignmentId);
    try {
      await conn.reducers.joinSessionAsObserver({ assignmentId: s.assignmentId });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast({ title: 'Observer join refused', sub: msg.slice(0, 200), kind: 'bad' });
      setJoinBusyId(null);
      return;
    }
    const res = await launch.launchWithArgs(
      buildObserverLaunchArgs({ ...launch.argContext, sessionId: s.sessionId }),
    );
    setJoinBusyId(null);
    if (res.ok) {
      toast({
        title: `Joining ${s.email}'s session as observer`,
        sub: res.downloading
          ? 'The launcher is downloading the client first — it opens when done.'
          : 'The MineZero client is opening in observer mode.',
        kind: 'good',
      });
    } else {
      toast({
        title: 'Observer recorded, launch pending',
        sub: res.message ?? 'The client could not be launched.',
        kind: 'warn',
      });
    }
  }

  const orientationNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of orientations) m.set(o.orientationId, o.displayName);
    return m;
  }, [orientations]);

  // Group runs render as ONE row each (Phase 5): live runs of this site,
  // respecting the cohort filter via their pairing id. Their members'
  // individual assignment rows are then EXCLUDED from the solo table so a
  // five-person group is one row, not six.
  const { runs: allGroupRuns } = useGroupRuns(currentSite?.projectName);
  const liveGroupRuns = useMemo(() => {
    const pairingIds = pairingIdsOf(cohorts, cohortFilter);
    return allGroupRuns.filter(
      (r) =>
        (r.statusTag === 'Running' || r.statusTag === 'Paused') &&
        inCohortFilter(r.cohortAssignmentId, pairingIds),
    );
  }, [allGroupRuns, cohorts, cohortFilter]);

  const visible = useMemo(() => {
    const groupSessionIds = new Set(liveGroupRuns.map((r) => r.sessionId.toString()));
    const siteScoped = currentSite
      ? sessions.filter((s) => s.projectId === currentSite.projectName)
      : sessions;
    const pairingIds = pairingIdsOf(cohorts, cohortFilter);
    return siteScoped.filter(
      (s) =>
        inCohortFilter(s.cohortAssignmentId, pairingIds) &&
        !groupSessionIds.has(s.sessionId.toString()),
    );
  }, [sessions, currentSite, cohorts, cohortFilter, liveGroupRuns]);

  /** Observer-join a group run through any member's assignment. */
  async function handleJoinGroup(run: GroupRunRowData) {
    // The run's leader launches straight in: the module admits them as
    // Participant on connect, and an observer grant would race that (#168).
    if (isRunLeader(run.leaderEmail, currentUserEmail)) {
      const res = await launch.launchWithArgs(
        buildObserverLaunchArgs({ ...launch.argContext, sessionId: run.sessionId }),
      );
      toast(
        res.ok
          ? { title: `Entering "${run.name}" as leader`, sub: 'The MineZero client is opening in the group session.', kind: 'good' }
          : { title: 'Launch pending', sub: res.message ?? 'The client could not be launched.', kind: 'warn' },
      );
      return;
    }
    const member = run.members[0];
    if (!member) return;
    void handleJoin({
      assignmentId: member.assignmentId,
      email: `group "${run.name}"`,
      sessionId: run.sessionId,
    } as LiveSession);
  }

  const connOnline = minetwinState.isActive;

  if (sitesReady && sites.length === 0) return <NoProjects pageName="Live Sessions" />;
  if (membership.isReady && currentSite && !membership.isMember)
    return <NoSiteMembership pageName="Live Sessions" siteName={currentSite.name} />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 style={{ margin: 0, fontSize: 18 }}>Live Sessions</h1>
          <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 2 }}>
            {visible.length} in flight
            {currentSite ? <> · {currentSite.name}</> : null}
            {connOnline ? null : <span style={{ color: 'var(--bad)' }}> · offline</span>}
          </div>
        </div>
        <div className="page-actions">
          <CohortFilterSelect
            cohorts={cohorts}
            value={cohortFilter}
            onChange={setCohortFilter}
          />
        </div>
      </div>

      <div className="page-body">
        {liveGroupRuns.length > 0 ? (
          <div className="card" style={{ marginBottom: 10 }}>
            <div className="card-head" style={{ padding: '10px 12px' }}>
              <h3 style={{ margin: 0, fontSize: 13 }}>Group runs</h3>
            </div>
            {liveGroupRuns.map((run) => (
              <GroupRunLiveRow
                key={String(run.groupRunId)}
                run={run}
                stepLabel={run.currentStepId ? (stepNameById.get(run.currentStepId) ?? run.currentStepId) : null}
                joinBusy={joinBusyId === run.members[0]?.assignmentId}
                joinDisabledReason={
                  run.sessionId === 0n
                    ? 'The shared session is still provisioning.'
                    : !launch.canLaunch
                      ? 'Start the launcher (topbar rocket) to join in-client.'
                      : null
                }
                onJoin={() => void handleJoinGroup(run)}
                cancelBusy={cancelBusyId === run.groupRunId}
                onCancel={() => void handleCancelGroup(run)}
              />
            ))}
          </div>
        ) : null}
        <div className="card">
          {!isReady ? (
            <div style={{ padding: 18, textAlign: 'center', color: 'var(--muted)', fontSize: 12 }}>
              Loading…
            </div>
          ) : visible.length === 0 ? (
            <div style={{ padding: 22, textAlign: 'center', color: 'var(--muted)', fontSize: 12 }}>
              {liveGroupRuns.length > 0
                ? 'No solo runs — everyone in flight is in a group above.'
                : currentSite
                  ? `No orientations in progress at ${currentSite.name}.`
                  : 'No orientations in progress.'}
            </div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Trainee</th>
                  <th>Orientation</th>
                  <th>Step</th>
                  <th>Last updated</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {visible.map((s) => (
                  <SessionRow
                    key={s.assignmentId.toString()}
                    session={s}
                    orientationName={
                      orientationNameById.get(s.orientationId) ?? s.orientationId
                    }
                    steps={stepsByOrientation.get(s.orientationId) ?? []}
                    joinBusy={joinBusyId === s.assignmentId}
                    joinDisabledReason={
                      s.sessionId === 0n
                        ? 'The session is still being provisioned — try again in a moment.'
                        : !launch.canLaunch
                          ? 'Start the launcher (topbar rocket) to join in-client.'
                          : null
                    }
                    onJoin={() => void handleJoin(s)}
                    cancelBusy={cancelBusyId === s.assignmentId}
                    onCancel={() => void handleCancelSolo(s)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}

function SessionRow({
  session: s,
  orientationName,
  steps,
  joinBusy,
  joinDisabledReason,
  onJoin,
  cancelBusy,
  onCancel,
}: {
  session: LiveSession;
  orientationName: string;
  steps: ReturnType<typeof useOrientationStepOrder>['stepsByOrientation'] extends Map<string, infer V> ? V : never;
  joinBusy: boolean;
  /** Non-null → the Join button renders disabled with this tooltip. */
  joinDisabledReason: string | null;
  onJoin: () => void;
  cancelBusy: boolean;
  onCancel: () => void;
}) {
  const currentIdx = s.currentStepId
    ? steps.findIndex((step) => step.stepId === s.currentStepId)
    : -1;
  const currentStep = currentIdx >= 0 ? steps[currentIdx] : null;
  const totalSteps = steps.length;
  const stepLabel = currentStep
    ? `${currentStep.displayName} (${currentIdx + 1}/${totalSteps})`
    : totalSteps > 0
      ? `(awaiting start, ${totalSteps} steps)`
      : '—';

  return (
    <tr>
      <td>
        <b>{s.email}</b>
        <div style={{ fontSize: 11, color: 'var(--muted)' }}>
          {s.userId ?? <i>pending sign-in</i>}
        </div>
      </td>
      <td>
        {orientationName}
        <div style={{ fontSize: 11, color: 'var(--muted)' }}>
          {/* Single-length mode: only a legacy timed tag is worth showing. */}
          {formatLegacyVersionTag(s.orientationVersion.tag)
            ? `${formatLegacyVersionTag(s.orientationVersion.tag)} · `
            : ''}
          {s.projectId}
        </div>
      </td>
      <td>{stepLabel}</td>
      <td className="mono" style={{ fontSize: 12 }}>
        {formatUpdatedAt(s.enteredCurrentStepAtMs ?? s.startedAtMs ?? s.assignedAtMs)}
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <GatedButton
          className="btn sm ghost"
          busy={joinBusy}
          reason={joinDisabledReason}
          hint="Watch this run from inside the MineZero client — free camera, read-only chat, no effect on the trainee."
          onClick={onJoin}
        >
          {joinBusy ? 'Joining…' : 'Join'}
        </GatedButton>{' '}
        <button
          type="button"
          className="btn sm danger"
          disabled={cancelBusy}
          title="Cancel this orientation: the session closes and progress stops."
          onClick={onCancel}
        >
          {cancelBusy ? 'Cancelling…' : 'Cancel'}
        </button>
      </td>
    </tr>
  );
}

/** Absolute date/time of the session's most recent update — its latest step
 *  transition, falling back to when it started, then when it was assigned. */
function formatUpdatedAt(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}
